import { logAmplitudeEvent } from 'amplitude/amplitude'
import { orgSelectors } from 'ducks/orgs'
import { DataProviderProxy } from 'ra-core'
import { Dispatch } from 'redux'
import { isActionWhitelisted } from '../actionPermissions'
import { AdaAction } from '../actions'
import { InverterDesignAssistantArgs, runInverterDesignAssistantCore } from '../utils/runInverterDesignAssistantCore'
import { createToolExecutionStateHelpers } from './hooks/useToolExecutionState'

function validateInputs(orgId: number | undefined, system: any): void {
  if (!orgId) {
    throw new Error('Organization ID is required to run inverter design assistant')
  }

  if (!system) {
    throw new Error('No system selected. Please select a system in the design.')
  }
}

function getOrgContext(): { countryIso2: string; orderByCodes: string[] } {
  const state = window.reduxStore?.getState()
  const countryIso2 = state ? orgSelectors.getOrgIso2(state) || '' : ''
  const orderByCodes: string[] = [] // Default empty, could be enhanced to use useRecommendedAndRequiredComponentOrdering
  return { countryIso2, orderByCodes }
}

function buildComponentQueryParams(params: any): URLSearchParams {
  const queryParams = new URLSearchParams()
  queryParams.append('page', '1')
  queryParams.append('limit', String(params.limit))
  queryParams.append('ordering', '_default_rule')
  queryParams.append('fieldset', 'list')
  queryParams.append('types', JSON.stringify(['inverter']))
  queryParams.append('show_archived', 'false')

  if (params.inverterType) {
    queryParams.append('inverter_type', params.inverterType)
  }

  if (params.countryIso2) {
    queryParams.append('country_iso2', params.countryIso2)
  }

  queryParams.append('order_by_codes', JSON.stringify(params.orderByCodes))

  if (params.manufacturerName) {
    queryParams.append('search', params.manufacturerName)
  } else if (params.codes && params.codes.length > 0) {
    queryParams.append('codes', JSON.stringify(params.codes))
  }

  return queryParams
}

function createComponentDataProviderCallback(
  orgId: number,
  countryIso2: string,
  orderByCodes: string[],
  dataProvider: DataProviderProxy
) {
  return async (params: any) => {
    params.orgId = orgId
    params.countryIso2 = countryIso2
    params.orderByCodes = orderByCodes

    const queryParams = buildComponentQueryParams(params)

    const response = await dataProvider.CUSTOM_POST('componentsv2', {
      url: `componentsv2/?${queryParams.toString()}`,
      data: {
        auto_string: params.autoStringParams,
      },
    })

    return (response?.data as any) || []
  }
}

function handleSuccess(toolState: any, result: any) {
  toolState.setReady(result.description, { inverterSets: result.inverterSets })
  console.log('Inverter design assistant completed, recommendations:', result.inverterSets.length)
}

function handleError(toolState: any, error: any) {
  console.error('Error running inverter design assistant:', error)
  const errorMessage = error?.message || 'Inverter design assistant failed'
  toolState.setError(errorMessage, {
    message: errorMessage,
  })
}

export async function handleRunInverterDesignAssistant(
  action: AdaAction,
  orgId: number | undefined,
  projectId: number | undefined,
  dataProvider: DataProviderProxy,
  dispatch: Dispatch
) {
  const isAutoAccepted = isActionWhitelisted('runInverterDesignAssistant')

  const toolState = createToolExecutionStateHelpers(dispatch, {
    toolType: 'inverterDesignAssistant',
    projectId,
    isAutoAccepted,
    initialContent: 'Getting inverter recommendations...',
  })

  try {
    const system = window.editor?.selectedSystem
    validateInputs(orgId, system)

    const args = action.args as InverterDesignAssistantArgs
    console.log('Starting runInverterDesignAssistant for system:', system.uuid, args)

    logAmplitudeEvent('generic_button_clicked', {
      source: 'Inverter Design Assistant Clicked',
      context: 'ada_chat_action',
      system_uuid: system.uuid,
      inverter_type: args.inverter_type,
      orgId: orgId,
    })

    toolState.setRunning()

    const { countryIso2, orderByCodes } = getOrgContext()
    const dataProviderCallback = createComponentDataProviderCallback(orgId!, countryIso2, orderByCodes, dataProvider)

    const result = await runInverterDesignAssistantCore(args, system.uuid, dataProviderCallback)

    handleSuccess(toolState, result)
  } catch (error: any) {
    handleError(toolState, error)
  }
}
